<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { showToast } from 'vant'
import { TheaterFeed, type IRefVideoItem, type IWrapEpisodeInfo } from '@/components/feed-player/index'
import { ChevronLeft, ChevronUp, AlertCircle, Minimize } from 'lucide-vue-next'
import { apiGetTheaterInfo } from '@/apis/theater'
import { useLikeStore } from '@/stores/like'
import { useCollectStore } from '@/stores/collect'
import { useAppStore } from '@/stores/app'
import { formattedVideoItem } from '@/components/feed-player/data-adapter'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const likeStore = useLikeStore()
const collectStore = useCollectStore()
const appStore = useAppStore()

const scriptId = ref(route.params.id as string)
const showDetailPopup = ref(false)
const theaterFeedRef = ref<InstanceType<typeof TheaterFeed> | null>(null)
const playEpisodeIndex = ref(0)

const isTitleExpanded = ref(false)
const isTitleOverflow = ref(false)
const titleRef = ref<HTMLElement | null>(null)

const theaterInfo = ref<Awaited<ReturnType<typeof apiGetTheaterInfo>> | null>(null)
const loading = ref(false)

const formatedVideoItems = computed<IRefVideoItem[]>(() => {
  const tags = theaterInfo.value?.Body.CategoryTags ?? []
  const data = theaterInfo.value?.Body
  if (!data) return []
  const episodes = data.Episodes.map<IWrapEpisodeInfo>(episode => ({
    ...episode,
    CategoryTags: tags,
    ScriptIdKey: data.IdKey,
    ScriptName: data.Name,
    ScriptId: data.Id,
    TotalEpisodes: data.TotalEpisodes,
    Description: data.Description,
  }))
  return episodes.map(episode => formattedVideoItem(episode, likeStore, collectStore))
})

// 当前播放的剧集信息
const currentEpisode = computed(() => {
  const data = theaterInfo.value?.Body
  if (!data) return null
  const episodes = data.Episodes || []
  return episodes[playEpisodeIndex.value]
})

// 返回上一页
const handleBack = () => {
  router.back()
}

// 监听当前播放索引变化
const handleIndexChange = (index: number) => {
  playEpisodeIndex.value = index
  isTitleExpanded.value = false // 切换视频时重置展开状态
  checkTitleOverflow() // 检测新视频标题是否溢出
}

// 切换标题展开状态
const toggleTitleExpand = () => {
  isTitleExpanded.value = !isTitleExpanded.value
}

// 检测标题是否溢出
const checkTitleOverflow = () => {
  nextTick(() => {
    if (titleRef.value) {
      isTitleOverflow.value = titleRef.value.scrollHeight > titleRef.value.clientHeight
    }
  })
}

const toggleEpisodePanel = (scriptId?: number) => {
  if (!scriptId) return
  appStore.toggleShowEpisode(scriptId)
}

onMounted(async () => {
  // 重置面板状态，防止从其他页面跳转时 activeMobilePanel 残留导致 navigation-enabled=false
  appStore.closeMobilePanel()

  const episodeQuery = (route.query.episode ?? route.query.episodeId) as string | undefined
  const targetEpisodeId = Number(episodeQuery)

  try {
    loading.value = true
    const response = await apiGetTheaterInfo({ scriptIdKey: scriptId.value })
    theaterInfo.value = response

    if (!response.IsSuccess || !response.Body) {
      showToast('加载失败，请重试')
      return
    }

    let episodeIndex =
      Number.isFinite(targetEpisodeId) && targetEpisodeId > 0
        ? response.Body.Episodes.findIndex(ep => ep.Id === targetEpisodeId)
        : 0
    episodeIndex = episodeIndex < 0 ? 0 : episodeIndex
    playEpisodeIndex.value = episodeIndex

    // 等待 v-if 渲染完成后再跳集，否则 theaterFeedRef 为 null
    await nextTick()
    theaterFeedRef.value?.jumpToIndex(episodeIndex)
    checkTitleOverflow() // 初始加载时检测标题溢出
  } catch (err) {
    console.error('获取短剧详情失败:', err)
    showToast('加载失败，请重试')
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  appStore.closeMobilePanel()
})

const onSelectEpisode = (episodeId: number) => {
  const data = theaterInfo.value?.Body
  if (!data) return
  const episodeIndex = data.Episodes.findIndex(ep => ep.Id === episodeId)
  if (episodeIndex >= 0) {
    theaterFeedRef.value?.jumpToIndex(episodeIndex)
    playEpisodeIndex.value = episodeIndex
    showDetailPopup.value = false
  }
}
</script>

<template>
  <div class="video-detail h-full bg-gray-950 text-white">
    <!-- 加载中状态 -->
    <div v-if="loading" class="flex items-center justify-center h-full">
      <van-loading />
    </div>

    <!-- 主内容区域 -->
    <div v-else-if="theaterInfo?.IsSuccess && theaterInfo.Body" class="relative h-full">
      <!-- 顶部导航 -->
      <div class="absolute top-0 left-0 z-40 flex items-center justify-between px-2 py-4">
        <div class="flex items-center gap-4">
          <ChevronLeft @click="handleBack" :size="28" :stroke-width="2" class="cursor-pointer" />
          <span class="text-lg">{{
            t('component.playletViewer.episode', { num: currentEpisode?.SequenceNumber })
          }}</span>
        </div>
      </div>

      <!-- 视频播放器 -->
      <TheaterFeed
        v-if="formatedVideoItems.length > 0"
        ref="theaterFeedRef"
        :items="formatedVideoItems"
        preset="playlet-detail"
        :playback-enabled="true"
        :navigation-enabled="appStore.activeMobilePanel === 'none'"
        :show-comments="true"
        :show-episodes="true"
        @index-change="handleIndexChange"
        @select-episode="onSelectEpisode"
      >
        <template v-if="appStore.isMobile" #footer="{ videoInfo }">
          <div class="flex h-12 flex-col bg-gray-950 relative">
            <div class="w-full flex flex-col absolute top-0 -translate-y-full">
              <div class="py-4 flex flex-col gap-4 px-4 max-w-4/5">
                <div
                  ref="titleRef"
                  class="uppercase text-sm text-justify transition-all duration-200 ease-in-out"
                  :class="[
                    isTitleExpanded ? 'max-h-[60vh] overflow-y-auto' : 'max-h-[2.8em] overflow-hidden',
                    isTitleOverflow ? 'pr-8' : '',
                  ]"
                >
                  {{ videoInfo?.title }}
                </div>

                <ChevronUp
                  v-if="isTitleOverflow"
                  @click="toggleTitleExpand"
                  class="absolute right-4 bottom-0 transition-transform duration-300"
                  :class="{ 'rotate-180': isTitleExpanded }"
                  :size="20"
                />
              </div>
              <div class="flex items-center gap-2 px-4 my-3">
                <span
                  class="backdrop-blur-xs bg-white/40 rounded-md px-1.5 py-1 text-xs text-white"
                  v-for="tag in videoInfo?.tags"
                  :key="tag"
                  >{{ tag }}</span
                >
              </div>
            </div>
            <div class="h-12 px-4 flex items-center justify-center gap-4 cursor-pointer">
              <div
                class="px-3 bg-white/10 h-9 rounded-xl flex items-center justify-between gap-4 flex-1"
                @click.stop="toggleEpisodePanel(videoInfo?.scriptId)"
              >
                <div class="flex items-center gap-2 flex-1">
                  <Layers :size="18" />
                  {{ t('component.playletViewer.selectEpisode') }} ● {{ theaterInfo.Body.StatusName }} ●
                  {{ t('component.playletViewer.totalEpisodes', { num: theaterInfo.Body.TotalEpisodes }) }}
                </div>
                <ChevronUp :class="{ 'rotate-180': showDetailPopup }" class="transition-transform" />
              </div>
              <Minimize :size="26" />
              <!-- <Maximize  v-else :size="26" @click="isFullscreen = true" /> -->
            </div>
          </div>
        </template>
      </TheaterFeed>

      <div
        v-else
        v-if="!loading"
        class="flex flex-col gap-4 text-gray-400 items-center justify-center min-h-screen p-6"
      >
        <AlertCircle :size="64" />
        <p class="text-sm text-gray-400">{{ t('component.playletViewer.notAvailable') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 隐藏滚动条 */
::-webkit-scrollbar {
  display: none;
}

/* 自定义遮罩层样式 */
:deep(.custom-overlay) {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

/* 滚动条样式 */
.h-full::-webkit-scrollbar {
  display: none;
}

/* popup内容容器 */
.h-full {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
